import React, { useEffect, useState, useCallback } from 'react';

const ACCENT = '#4A7C59';

const STATUS_LABEL: Record<string, string> = {
  cherry_intake: 'Cherry intake',
  fermentation: 'Fermentation',
  drying: 'Drying',
  milling: 'Milling',
  grading: 'Grading',
  complete: 'Complete',
};

const PROCESS_LABEL: Record<string, string> = {
  washed: 'Washed',
  natural: 'Natural',
  honey: 'Honey',
  mbuni: 'Mbuni',
};

const GRADE_ORDER = ['AA', 'AB', 'PB', 'C', 'TT', 'UG', 'MH', 'ML'];

// Kenyan shilling formatter
const fmt = (n: number) => n.toLocaleString('en-KE', { maximumFractionDigits: 0 });
const fmtKg = (n: number) => `${fmt(n)} kg`;

interface ReportData {
  deliveries: {
    summary: { totalDeliveries: number; totalCherryKg: number; totalValue: number };
    byQuality: { quality: string; deliveries: number; totalKg: number }[];
    byCentre: { centreName: string; deliveries: number; totalKg: number; totalValue: number }[];
  };
  batches: {
    byStatus: { status: string; count: number; totalCherryKg: number }[];
    byProcess: { processType: string; count: number; totalGrossKg: number; totalCherryKg: number; totalMbuniKg: number; totalWasteKg: number }[];
  };
  mbuni: { totalGrossKg: number; totalMbuniKg: number; totalWasteKg: number; diversionPct: string | null; wastePct: string | null };
  milling: { totalRuns: number; totalParchmentKg: number; totalGreenKg: number; avgOutturnPct: string | null };
  grading: { breakdown: { grade: string; totalKg: number; runs: number }[]; totalGradedKg: number };
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white border border-[#E8E6E2] rounded-xl p-5">
      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-bold text-[#1A1A1A]">{value}</p>
      {sub && <p className="text-xs text-[#6B6B6B] mt-0.5">{sub}</p>}
    </div>
  );
}

export const ReportsView: React.FC = () => {
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    try {
      const res = await fetch(`/api/production/reports?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setData(json);
    } catch {
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const inputCls = "px-3 py-1.5 border border-[#E8E6E2] rounded-lg text-sm bg-white text-[#1A1A1A] focus:outline-none";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A]">Season report</h2>
          <p className="text-sm text-[#6B6B6B] mt-0.5">Cherry → green coffee pipeline summary</p>
        </div>
        {/* Date range filter */}
        <div className="flex items-center gap-2">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inputCls} />
          <span className="text-sm text-[#A3A3A3]">to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inputCls} />
          <button
            onClick={() => { setFrom(''); setTo(''); }}
            className="px-3 py-1.5 text-xs text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]"
          >
            Clear
          </button>
        </div>
      </div>

      {loading && <div className="py-16 text-center text-sm text-[#6B6B6B]">Loading report...</div>}
      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

      {!loading && data && (
        <div className="space-y-8">

          {/* ── Cherry Deliveries ─────────────────────────────────── */}
          <section>
            <h3 className="text-xs font-medium text-[#A3A3A3] uppercase tracking-wider mb-3">Cherry intake</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <StatCard
                label="Total deliveries"
                value={fmt(data.deliveries.summary.totalDeliveries)}
                sub="farmer deliveries"
              />
              <StatCard
                label="Total cherry received"
                value={fmtKg(data.deliveries.summary.totalCherryKg)}
                sub="before sorting"
              />
              <StatCard
                label="Total value paid"
                value={`KES ${fmt(data.deliveries.summary.totalValue)}`}
                sub="to farmers"
              />
            </div>

            {/* Quality breakdown */}
            {data.deliveries.byQuality.length > 0 && (
              <div className="bg-white border border-[#E8E6E2] rounded-xl p-5 mb-4">
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-3">By quality grade</p>
                <div className="space-y-2">
                  {data.deliveries.byQuality.map(q => {
                    const pct = data.deliveries.summary.totalCherryKg > 0
                      ? (q.totalKg / data.deliveries.summary.totalCherryKg) * 100
                      : 0;
                    return (
                      <div key={q.quality} className="flex items-center gap-3">
                        <span className="w-8 text-sm font-bold text-[#1A1A1A]">{q.quality}</span>
                        <div className="flex-1 bg-[#F0EDE8] rounded-full h-2 overflow-hidden">
                          <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: ACCENT }} />
                        </div>
                        <span className="w-24 text-right text-xs text-[#6B6B6B]">{fmtKg(q.totalKg)}</span>
                        <span className="w-10 text-right text-xs text-[#A3A3A3]">{pct.toFixed(0)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* By centre */}
            {data.deliveries.byCentre.length > 0 && (
              <div className="bg-white border border-[#E8E6E2] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E6E2] bg-[#F8F7F5]">
                      <th className="text-left px-4 py-2.5 text-xs text-[#A3A3A3] font-medium uppercase tracking-wider">Collection centre</th>
                      <th className="text-right px-4 py-2.5 text-xs text-[#A3A3A3] font-medium uppercase tracking-wider">Deliveries</th>
                      <th className="text-right px-4 py-2.5 text-xs text-[#A3A3A3] font-medium uppercase tracking-wider">Total kg</th>
                      <th className="text-right px-4 py-2.5 text-xs text-[#A3A3A3] font-medium uppercase tracking-wider">Value (KES)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8E6E2]">
                    {data.deliveries.byCentre.map(c => (
                      <tr key={c.centreName} className="hover:bg-[#F8F7F5]">
                        <td className="px-4 py-3 font-medium text-[#1A1A1A]">{c.centreName}</td>
                        <td className="px-4 py-3 text-right text-[#6B6B6B]">{fmt(c.deliveries)}</td>
                        <td className="px-4 py-3 text-right text-[#6B6B6B]">{fmtKg(c.totalKg)}</td>
                        <td className="px-4 py-3 text-right text-[#6B6B6B]">{fmt(c.totalValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Mbuni Diversion ────────────────────────────────────── */}
          <section>
            <h3 className="text-xs font-medium text-[#A3A3A3] uppercase tracking-wider mb-3">Mbuni diversion (sorting)</h3>
            <div className="grid grid-cols-4 gap-4">
              <StatCard
                label="Total gross intake"
                value={fmtKg(data.mbuni.totalGrossKg)}
                sub="W_Total across all batches"
              />
              <StatCard
                label="Primary cherry"
                value={fmtKg(data.mbuni.totalGrossKg - data.mbuni.totalMbuniKg - data.mbuni.totalWasteKg)}
                sub="W_Primary → pulping"
              />
              <StatCard
                label="Mbuni diverted"
                value={data.mbuni.diversionPct ? `${data.mbuni.diversionPct}%` : '—'}
                sub={fmtKg(data.mbuni.totalMbuniKg)}
              />
              <StatCard
                label="Waste"
                value={data.mbuni.wastePct ? `${data.mbuni.wastePct}%` : '—'}
                sub={fmtKg(data.mbuni.totalWasteKg)}
              />
            </div>
          </section>

          {/* ── Batch Pipeline ─────────────────────────────────────── */}
          <section>
            <h3 className="text-xs font-medium text-[#A3A3A3] uppercase tracking-wider mb-3">Processing batches</h3>
            <div className="grid grid-cols-2 gap-4">
              {/* By status */}
              <div className="bg-white border border-[#E8E6E2] rounded-xl p-5">
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-3">By pipeline stage</p>
                <div className="space-y-2">
                  {['cherry_intake', 'fermentation', 'drying', 'milling', 'grading', 'complete'].map(s => {
                    const row = data.batches.byStatus.find(b => b.status === s);
                    if (!row) return null;
                    return (
                      <div key={s} className="flex items-center justify-between">
                        <span className="text-sm text-[#1A1A1A]">{STATUS_LABEL[s]}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-[#A3A3A3]">{fmtKg(row.totalCherryKg)}</span>
                          <span className="w-8 text-right text-sm font-bold text-[#1A1A1A]">{row.count}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* By process type */}
              <div className="bg-white border border-[#E8E6E2] rounded-xl p-5">
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-3">By process type</p>
                <div className="space-y-3">
                  {data.batches.byProcess.map(p => {
                    const primaryKg = p.totalGrossKg - p.totalMbuniKg - p.totalWasteKg;
                    const yieldPct = p.totalGrossKg > 0 ? ((primaryKg / p.totalGrossKg) * 100).toFixed(0) : null;
                    return (
                      <div key={p.processType}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm font-medium text-[#1A1A1A]">{PROCESS_LABEL[p.processType] || p.processType}</span>
                          <span className="text-xs text-[#6B6B6B]">{p.count} batch{p.count !== 1 ? 'es' : ''}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-[#A3A3A3]">
                          <span>{fmtKg(p.totalCherryKg)} cherry</span>
                          {yieldPct && p.processType !== 'mbuni' && (
                            <span className="text-[#4A7C59] font-medium">{yieldPct}% primary yield</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* ── Milling & Green Coffee ─────────────────────────────── */}
          <section>
            <h3 className="text-xs font-medium text-[#A3A3A3] uppercase tracking-wider mb-3">Milling & green coffee</h3>
            <div className="grid grid-cols-4 gap-4">
              <StatCard label="Milling runs" value={fmt(data.milling.totalRuns)} />
              <StatCard label="Parchment milled" value={fmtKg(data.milling.totalParchmentKg)} />
              <StatCard label="Green coffee" value={fmtKg(data.milling.totalGreenKg)} />
              <StatCard
                label="Avg. outturn"
                value={data.milling.avgOutturnPct ? `${data.milling.avgOutturnPct}%` : '—'}
                sub="parchment → green"
              />
            </div>
          </section>

          {/* ── Grade Breakdown ────────────────────────────────────── */}
          {data.grading.breakdown.length > 0 && (
            <section>
              <h3 className="text-xs font-medium text-[#A3A3A3] uppercase tracking-wider mb-3">
                Grade breakdown — {fmtKg(data.grading.totalGradedKg)} total
              </h3>
              <div className="bg-white border border-[#E8E6E2] rounded-xl p-5">
                <div className="space-y-3">
                  {GRADE_ORDER.map(grade => {
                    const row = data.grading.breakdown.find(g => g.grade === grade);
                    if (!row || row.totalKg === 0) return null;
                    const pct = data.grading.totalGradedKg > 0
                      ? (row.totalKg / data.grading.totalGradedKg) * 100
                      : 0;
                    const isMbuni = ['MH', 'ML'].includes(grade);
                    return (
                      <div key={grade} className="flex items-center gap-3">
                        <span className={`w-10 text-sm font-mono font-bold ${isMbuni ? 'text-amber-700' : 'text-[#1A1A1A]'}`}>
                          {grade}
                        </span>
                        <div className="flex-1 bg-[#F0EDE8] rounded-full h-2.5 overflow-hidden">
                          <div
                            className="h-2.5 rounded-full"
                            style={{ width: `${pct}%`, backgroundColor: isMbuni ? '#D97706' : ACCENT }}
                          />
                        </div>
                        <span className="w-28 text-right text-sm font-medium text-[#1A1A1A]">{fmtKg(row.totalKg)}</span>
                        <span className="w-12 text-right text-xs text-[#A3A3A3]">{pct.toFixed(1)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {data.grading.breakdown.length === 0 && (
            <div className="py-8 text-center text-sm text-[#A3A3A3]">
              No grading data yet — grade breakdown will appear after milling runs are recorded
            </div>
          )}
        </div>
      )}
    </div>
  );
};
